import { InlineAlertType } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Redirect,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import { isValidAmountParam } from 'features/billSwitch/helpers/amountValidator';
import { numberValue } from 'features/billSwitch/helpers/numberValue';
import { useDebitCardOptions } from 'features/moveMoney/screens/dcf/hooks/useDebitCardOptions';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import ListItemGroup, {
  ListItemGroupItem,
} from 'shared/components/vxl/ListItemGroup/ListItemGroup';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { TagType } from 'shared/components/vxl/Tag/Tag';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleXLText from 'shared/components/vxl/typography/primary/title/TitleXLText';
import { redirectToHomePage } from 'shared/helpers/backNavigation.helpers';
import { useViewDidFocusLogScreenAnalytics } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import useIsFeatureEnabled from 'shared/hooks/statsig/useIsFeatureEnabled/useIsFeatureEnabled';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

const BillSwitchFundingScreen = () => {
  const { t: translatedText } = useTranslation('billSwitch', {
    keyPrefix: 'fundingScreen',
  });
  const router = useRouter();
  const { top, bottom } = useSafeAreaInsets();
  const isBillSwitchEnabled = useIsFeatureEnabled(
    FeatureFlagKey.BILL_SWITCH_PILOT,
  );
  const isAmountAlertEnabled = useIsFeatureEnabled(
    FeatureFlagKey.BILL_SWITCH_FUNDING_ENABLE_AMOUNT_ALERT,
  );

  const { amt, source } = useLocalSearchParams<{
    amt?: string;
    source?: string;
  }>();
  const { loading, debitOptionError, isDcfAvailable, maxCardFundingAmount } =
    useDebitCardOptions();

  useViewDidFocusLogScreenAnalytics(
    isBillSwitchEnabled
      ? {
          name: 'Bill Switch Funding',
          properties: {
            category: 'bill_switch',
            screen: 'funding',
            source_name: source ?? 'unknown',
            estimated_bill_amount: amt ?? 'unknown',
          },
        }
      : null,
  );

  if (!isBillSwitchEnabled) {
    return <Redirect href="/" />;
  }

  const fundingOptions: ListItemGroupItem[] = [
    {
      keyId: 'p2p-funding',
      primaryCopy: translatedText('fundingOptions.p2p.title'),
      leadingAccessory: {
        id: 'p2p-icon',
        name: VXLComponentId.ICON_AVATAR,
        props: { variant: 'LG', illustrationKey: 'PHONE_A' },
      },
      trailingAccessory: {
        id: 'p2p-tag',
        name: VXLComponentId.TAG,
        props: {
          text: translatedText('fundingOptions.p2p.tag'),
          type: TagType.HIGHLIGHT,
        },
      },
      analyticsId: 'fundingOptionSelected',
      customAttributes: {
        source_name: source ?? 'unknown',
        funding_source: 'p2p',
      },
      onPress: () => {
        router.push('/move-money/p2p/debit');
      },
    },
    {
      keyId: 'transfer-funding',
      primaryCopy: translatedText('fundingOptions.transfer.title'),
      leadingAccessory: {
        id: 'transfer-icon',
        name: VXLComponentId.ICON_AVATAR,
        props: { variant: 'LG', illustrationKey: 'BANK_A' },
      },
      trailingAccessory: {
        id: 'transfer-tag',
        name: VXLComponentId.TAG,
        props: {
          text: translatedText('fundingOptions.transfer.tag'),
          type: TagType.INFO,
        },
      },
      analyticsId: 'fundingOptionSelected',
      customAttributes: {
        source_name: source ?? 'unknown',
        funding_source: 'bank_transfer',
      },
      onPress: () => {
        router.push('/move-money/transfer/transfer-money');
      },
    },
    {
      keyId: 'cash-deposit-funding',
      primaryCopy: translatedText('fundingOptions.cashDeposit.title'),
      leadingAccessory: {
        id: 'cash-deposit-icon',
        name: VXLComponentId.ICON_AVATAR,
        props: { variant: 'LG', illustrationKey: 'PHONE_A' },
      },
      trailingAccessory: {
        id: 'cash-deposit-tag',
        name: VXLComponentId.TAG,
        props: {
          text: translatedText('fundingOptions.cashDeposit.tag'),
          type: TagType.HIGHLIGHT,
        },
      },
      analyticsId: 'fundingOptionSelected',
      customAttributes: {
        source_name: source ?? 'unknown',
        funding_source: 'cash_deposit',
      },
      onPress: () => {
        router.push('/move-money/deposit-cash');
      },
    },
  ];

  const allowDcf =
    !loading &&
    !debitOptionError &&
    isDcfAvailable &&
    maxCardFundingAmount &&
    numberValue(maxCardFundingAmount.amount) > 0;

  if (allowDcf) {
    fundingOptions.push({
      keyId: 'dcf-funding',
      primaryCopy: translatedText('fundingOptions.dcf.title'),
      leadingAccessory: {
        id: 'dcf-icon',
        name: VXLComponentId.ICON_AVATAR,
        props: { variant: 'LG', illustrationKey: 'DEBIT_CARD_A' },
      },
      trailingAccessory: {
        id: 'dcf-tag',
        name: VXLComponentId.TAG,
        props: {
          text: translatedText('fundingOptions.dcf.tag'),
          type: TagType.HIGHLIGHT,
        },
      },
      analyticsId: 'fundingOptionSelected',
      customAttributes: {
        source_name: source ?? 'unknown',
        funding_source: 'dcf',
      },
      onPress: () => {
        router.push('/move-money/debit-card-funding');
      },
    });
  }

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryIcon={<Icon iconKey="CLOSE" iconSize="MD" />}
              primaryOnPress={redirectToHomePage}
            />
          ),
        }}
      />
      {!loading ? (
        <>
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollViewContentContainer}
          >
            <View style={styles.headerSectionContainer}>
              <TitleXLText>{translatedText('title')}</TitleXLText>
              <BodyBaseText>{translatedText('subtitle')}</BodyBaseText>
              {isAmountAlertEnabled && isValidAmountParam(amt) ? (
                <InlineAlert
                  alertType={InlineAlertType.Info}
                  isImageVisible={false}
                  titleText={translatedText('alert.title')}
                  bodyText={'$' + amt}
                />
              ) : null}
            </View>
            <View style={styles.divider} />
            <View style={styles.listSectionContainer}>
              <ListItemGroup items={fundingOptions} />
            </View>
          </ScrollView>
          <View
            style={[
              { paddingBottom: theme.spacing.m + bottom },
              styles.buttonContainer,
            ]}
          >
            <Button
              label={translatedText('cta.secondary')}
              type="SECONDARY"
              analyticsId="secondaryAction"
              customAttributes={{ source_name: source }}
              onPress={() => {
                redirectToHomePage();
              }}
            />
          </View>
        </>
      ) : null}
      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={
          <View style={{ paddingTop: top + theme.spacing.m }}>
            <LoadingSpinner />
          </View>
        }
        ErrorComponent={null}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    backgroundColor: theme.color.background.primary,
    borderTopColor: theme.color.border.primary,
    borderTopWidth: 1,
    padding: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  divider: {
    backgroundColor: theme.color.background.tertiary,
    height: theme.spacing.s,
  },
  headerSectionContainer: {
    backgroundColor: theme.color.background.primary,
    gap: theme.spacing.s,
    padding: theme.spacing.m,
  },
  listSectionContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContentContainer: {
    flex: 1,
  },
});

export default BillSwitchFundingScreen;
